import { ExpandLess, KeyboardArrowRight } from '@mui/icons-material';
import { Box, CircularProgress, Collapse, Divider, IconButton, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import useControllerPowerStateIcon from '../../../hooks/ui/useControllerPowerStateIcon';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { RobotActions } from '../../../utils/constants';
import ControllerIcon from '../assets/BPS_controller.svg';

interface ControllerInfoPanelProps {
  colorMode: string | undefined;
  expanded: boolean;
  onToggle: () => void;
}

const ControllerInfoPanel = ({ colorMode, expanded, onToggle }: ControllerInfoPanelProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { controllerConnected } = useAppSelector((state) => state.application);
  const {
    sku: controllerSku,
    boardRevision: controllerBoardRevision,
    firmwareBootloader: controllerFirmwareBootloader,
    mainAppVersion: controllerMainAppVersion,
    uid: controllerUid,
  } = useAppSelector((state) => state.controllerData);
  const firmwareUpdateState = useAppSelector((state) => state.robot.firmware.firmwareState);

  const getControllerFirmwareVersion = () => {
    if (controllerMainAppVersion) {
      return controllerMainAppVersion.toString();
    }
    return '';
  };

  const controllerFirmwareVersion = getControllerFirmwareVersion();
  const headerControllerIcon = useControllerPowerStateIcon(colorMode === 'dark' ? 'light' : 'dark');
  const hasAnyControllerInfo =
    controllerBoardRevision !== null || Boolean(controllerSku || controllerFirmwareBootloader || controllerFirmwareVersion || controllerUid);
  const hasCompleteControllerInfo =
    controllerBoardRevision !== null && Boolean(controllerSku && controllerFirmwareBootloader && controllerFirmwareVersion && controllerUid);
  const isFirmwareTransferActive =
    firmwareUpdateState === RobotActions.FIRMWARE_UPDATE.START_DOWNLOAD || firmwareUpdateState === RobotActions.FIRMWARE_UPDATE.STARTED;
  const showControllerDetails = hasAnyControllerInfo || isFirmwareTransferActive;

  useEffect(() => {
    if (!expanded || !controllerConnected || hasCompleteControllerInfo) {
      return;
    }

    window.robotService?.getNonPortRemoteDeviceSystemInfo?.();
  }, [controllerConnected, expanded, hasCompleteControllerInfo]);

  return (
    <Paper elevation={2} sx={{ marginTop: '17px' }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            aria-hidden
            sx={{
              width: 22,
              height: 22,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              flexShrink: 0,
              '& svg': {
                width: 22,
                height: 22,
                display: 'block',
              },
            }}
          >
            {headerControllerIcon}
          </Box>
          <Typography variant="subHeaderTitle" sx={{ fontSize: isSmallScreen ? '16px' : isMediumScreen ? '18px' : '20px' }}>
            <Trans i18nKey="connectedDrawer.controller-info.title">Controller</Trans>
          </Typography>
        </Stack>
        <IconButton size="small" aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <ExpandLess /> : <KeyboardArrowRight />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box padding="24px" paddingTop={0}>
          <Divider />
          {controllerConnected ? (
            showControllerDetails ? (
              <Stack direction="row" spacing={8} alignItems="center" textAlign="center" mt={3}>
                <Box>
                  <Box
                    component="img"
                    src={ControllerIcon}
                    alt="controller"
                    sx={{
                      width: '150px',
                      height: '150px',
                      filter: colorMode === 'dark' ? 'invert(1)' : 'none',
                    }}
                  />
                </Box>
                <Box flex="1">
                  <Stack direction="column" spacing={2} alignItems="flex-start" mt={3}>
                    <Typography variant="body1">
                      <Trans i18nKey="controllerInfo.device_pn">Device P/N: {{ devicePn: controllerSku || '' }}</Trans>
                    </Typography>
                    <Typography variant="body1">
                      <Trans i18nKey="controllerInfo.board_revision">Board Revision: {{ boardRevision: controllerBoardRevision ?? '' }}</Trans>
                    </Typography>
                    <Typography variant="body1">
                      <Trans i18nKey="controllerInfo.firmware_bootloader">
                        BL Firmware: {{ firmwareBootloader: controllerFirmwareBootloader || '' }}
                      </Trans>
                    </Typography>
                    <Typography variant="body1">
                      <Trans i18nKey="controllerInfo.firmware_main_app">Main Firmware: {{ firmwareMainApp: controllerFirmwareVersion }}</Trans>
                    </Typography>
                    {controllerUid && (
                      <Typography variant="body1">
                        <Trans i18nKey="controllerInfo.uid">UID: {{ uid: controllerUid }}</Trans>
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            )
          ) : (
            <Typography textAlign="center" pt={2}>
              <Trans i18nKey="connectedDrawer.controller.not-connected">A Controller is not connected to this robot</Trans>
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ControllerInfoPanel;
